import { createI18n } from "vue-i18n";
import en from "./locales/en.json";

/**
 * Slavic plural rules (Polish, Russian)
 * Returns: 0 = one, 1 = few (2-4), 2 = many (0, 5-20, etc.)
 */
const slavicPluralRule = (choice, choicesLength) => {
  if (choice === 0) {
    return 2; // "many" form for zero
  }

  const teen = choice > 10 && choice < 20;
  const endsWithOne = choice % 10 === 1;

  if (choicesLength < 4) {
    // For 2 or 3 choices, use simple logic
    if (!teen && endsWithOne) {
      return 0; // one
    }

    if (!teen && choice % 10 >= 2 && choice % 10 <= 4) {
      return choicesLength === 3 ? 1 : 0; // few
    }

    return choicesLength === 3 ? 2 : 1; // many
  }

  // For 4+ choices (rare), return based on index
  if (!teen && endsWithOne) {
    return 0;
  }

  if (!teen && choice % 10 >= 2 && choice % 10 <= 4) {
    return 1;
  }

  return 2;
};

const i18n = createI18n({
  legacy: false,
  locale: import.meta.env.VITE_DEFAULT_LOCALE || 'en',
  fallbackLocale: import.meta.env.VITE_FALLBACK_LOCALE || 'en',
  globalInjection: true,
  messages: {
    en,
  },
  pluralRules: {
    pl: slavicPluralRule,
    ru: slavicPluralRule,
  },
});

/**
 * Lazy load locale messages
 * @param {string} locale - Locale code (e.g., 'de', 'es')
 */
export const loadLocaleMessages = async (locale) => {
  if (i18n.global.availableLocales.includes(locale)) {
    return;
  }

  const messages = await import(`./locales/${locale}.json`);
  i18n.global.setLocaleMessage(locale, messages.default);
};

/**
 * Set the active locale with lazy loading
 * @param {string} locale - Locale code (e.g., 'de', 'es')
 */
export const setLocale = async (locale) => {
  await loadLocaleMessages(locale);
  i18n.global.locale.value = locale;
};

export default i18n;
